import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listLibraryItems } from '../../data/reference';
import { describeApiError } from '../../data/http';
import type { LibraryItem, LibraryType } from '../../data/types';

interface LibraryItemPickerProps {
  value: string;
  onChange: (item: LibraryItem | null) => void;
  idPrefix: string;
  libraryType: LibraryType;
}

/** TA-17: pick a template (or Document block) from the SharePoint library instead of uploading a copy. */
export function LibraryItemPicker({ value, onChange, idPrefix, libraryType }: LibraryItemPickerProps) {
  const { t, i18n } = useTranslation();

  const [items, setItems] = useState<LibraryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;

    listLibraryItems(libraryType)
      .then((loaded) => {
        if (!cancelled) setItems(loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeApiError(err, t('studio.library.unavailable')));
      });

    return () => {
      cancelled = true;
    };
  }, [t, libraryType]);

  if (error) {
    return (
      <p role="alert" className="border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    );
  }

  if (items === null) {
    return (
      <p className="text-sm text-muted-foreground" aria-live="polite">
        {t('studio.library.loading')}
      </p>
    );
  }

  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('studio.library.empty')}</p>;
  }

  const needle = search.trim().toLowerCase();
  const visible = needle ? items.filter((item) => item.name.toLowerCase().includes(needle)) : items;

  return (
    <div>
      <label htmlFor={`${idPrefix}-search`} className="block text-sm font-medium text-foreground">
        {t('studio.library.search')}
      </label>
      <input
        id={`${idPrefix}-search`}
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={t('studio.library.searchPlaceholder')}
        className="mt-1 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
      />

      {visible.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">{t('studio.library.noMatches')}</p>
      ) : (
        <ul className="mt-3 max-h-64 divide-y divide-border overflow-y-auto rounded-sm border border-border">
          {visible.map((item) => (
            <li key={item.id}>
              <label
                className={`flex cursor-pointer items-center gap-3 px-3 py-2 text-sm transition ${
                  value === item.id ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'
                }`}
              >
                <input
                  type="radio"
                  name={`${idPrefix}-library-item`}
                  value={item.id}
                  checked={value === item.id}
                  onChange={() => onChange(item)}
                  className="size-4 shrink-0 accent-primary"
                />
                <span className="min-w-0 flex-1 truncate font-medium text-foreground">{item.name}</span>
                {item.modified ? (
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {new Date(item.modified).toLocaleDateString(i18n.language)}
                  </span>
                ) : null}
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
